import $ from 'jquery';
import React from 'react';
import isEqual from 'react-fast-compare';

import DataConnector from 'connect/DataConnector';
import AaSequenceActions from 'molbio/aa-sequences/AaSequenceActions';
import AaSequenceStore, {SearchResult} from 'molbio/aa-sequences/AaSequenceStore';
import FullScreenStore from 'nav/full-screen/FullScreenStore';
import SidepanelStore from 'nav/sidepanel/SidepanelStore';
import CtrlFView from 'ui-library/CtrlFView';
import * as keyboard from 'util/Keyboard';

const AA_SEQUENCE_SEARCH_EVENT_NAMESPACE = 'protein-ctrl-f-view';

class AaSequenceCtrlFViewConnector extends DataConnector<AaSequenceCtrlFViewProps, EnhancedProps> {
  override shouldSubscribeToRedux = false;
  override stores = [AaSequenceStore, SidepanelStore, FullScreenStore];

  enhanceProps(): EnhancedProps {
    return {
      selectedIndex: this.aaSequenceStore().getSelectedSearchResultIndex(),
      selectedSearchResult: this.aaSequenceStore().getSelectedSearchResult(),
      numSearchResults: this.aaSequenceStore().getNumSearchResults(),
      pendingOpenSearchView: this.aaSequenceStore().getPendingOpenSearchView(),
      sidepanelIsExpanded: this.sidepanelStore().getIsExpanded(),
      fullScreenPageRendered: this.fullScreenStore().getCurrentScreen() != null,
    };
  }
}

export interface AaSequenceCtrlFViewProps {
  jumpToIndex: (index: number) => void;
}
interface EnhancedProps {
  selectedIndex: number;
  selectedSearchResult: SearchResult | undefined;
  numSearchResults: number;
  pendingOpenSearchView: boolean;
  sidepanelIsExpanded: boolean;
  fullScreenPageRendered: boolean;
}

export interface AaSequenceCtrlFViewState {
  query: string;
  visible: boolean;
}

export class AaSequenceCtrlFView extends React.Component<
  AaSequenceCtrlFViewProps & EnhancedProps,
  AaSequenceCtrlFViewState
> {
  override state: AaSequenceCtrlFViewState = {
    query: '',
    visible: false,
  };

  searchRef = React.createRef<CtrlFView>();

  override componentDidMount(): void {
    $(window).on(`keydown.${AA_SEQUENCE_SEARCH_EVENT_NAMESPACE}`, (e) => {
      if (keyboard.isCtrlF(e)) {
        if (this.props.sidepanelIsExpanded || this.props.fullScreenPageRendered) {
          return;
        }

        e.preventDefault();
        this._handleOpenSearchView();
      }
    });
  }

  override componentDidUpdate(
    prevProps: AaSequenceCtrlFViewProps & EnhancedProps,
    prevState: AaSequenceCtrlFViewState
  ): void {
    if (!prevState.visible && this.state.visible) {
      this._handleQueryChange(this.state.query);
    }

    const selectedSearchResultChanged = !isEqual(
      prevProps.selectedSearchResult,
      this.props.selectedSearchResult
    );
    if (this.props.selectedSearchResult != null && selectedSearchResultChanged) {
      this.props.jumpToIndex(this.props.selectedSearchResult.index);
    }
    if (this.props.pendingOpenSearchView) {
      this._handleOpenSearchView();
      AaSequenceActions.completeOpenSearchView();
    }
  }

  override componentWillUnmount(): void {
    $(window).off(`keydown.${AA_SEQUENCE_SEARCH_EVENT_NAMESPACE}`);
  }

  _handleOpenSearchView = (): void => {
    if (this.state.visible) {
      this.searchRef.current!.focusAndSelect();
    } else {
      this.show();
    }
  };

  _handleQueryChange = (query: string): void => {
    if (query.length === 0) {
      AaSequenceActions.clearSearchResults();
    } else {
      AaSequenceActions.searchAminoAcids(query);
    }
    this.setState({query});
  };

  _handleViewNextResult = (): void => {
    AaSequenceActions.viewNextSearchResult();
  };

  _handleViewPreviousResult = (): void => {
    AaSequenceActions.viewPreviousSearchResult();
  };

  hide = (): void => {
    AaSequenceActions.clearSearchResults();
    this.setState({visible: false});
  };

  _handleClose = (): void => {
    this.hide();
  };

  show = (): void => {
    this.setState({visible: true});
  };

  override render(): JSX.Element | null {
    if (!this.state.visible || this.props.sidepanelIsExpanded) {
      return null;
    }
    return (
      <CtrlFView
        ref={this.searchRef}
        onClose={this._handleClose}
        onQueryChange={this._handleQueryChange}
        query={this.state.query}
        onViewNextResult={this._handleViewNextResult}
        onViewPreviousResult={this._handleViewPreviousResult}
        selectedIndex={this.props.selectedIndex}
        numSearchResults={this.props.numSearchResults}
      />
    );
  }
}

export default AaSequenceCtrlFViewConnector.connect(AaSequenceCtrlFView);
